export interface JobSkillSuggestionSource {
  id?: string
  name: string
}

/** Merge org-used skills first, then global catalog; dedupe case-insensitively. */
export function buildJobSkillSuggestions({
  orgSkills,
  catalogSkills,
  excludeSkillIds,
}: {
  orgSkills: JobSkillSuggestionSource[]
  catalogSkills: JobSkillSuggestionSource[]
  excludeSkillIds?: string[]
}): { id?: string; name: string }[] {
  const excludedIds = new Set((excludeSkillIds ?? []).map((id) => id.trim()).filter(Boolean))
  const map = new Map<string, { id?: string; name: string }>()

  const add = (row: JobSkillSuggestionSource) => {
    const name = row.name.trim()
    if (!name) return
    if (row.id && excludedIds.has(row.id)) return
    const key = name.toLowerCase()
    if (!map.has(key)) {
      map.set(key, { id: row.id, name })
    } else if (row.id && !map.get(key)?.id) {
      map.set(key, { id: row.id, name: map.get(key)!.name })
    }
  }

  for (const row of orgSkills) add(row)
  for (const row of catalogSkills) add(row)

  return [...map.values()].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  )
}

/** Case-insensitive catalog lookup by name. */
export function resolveSkillByName(
  pool: JobSkillSuggestionSource[],
  rawName: string
): { id: string; name: string } | null {
  const query = rawName.trim().toLowerCase()
  if (!query) return null
  const match = pool.find((row) => row.name.trim().toLowerCase() === query)
  if (!match?.id) return null
  return { id: match.id, name: match.name.trim() }
}

export type PrepareRenameJobSkillResult =
  | { ok: true; trimmed: string }
  | { ok: false; error: string | null }

/** Validates rename draft for a job skill row (duplicate check excludes the row being edited). */
export function prepareRenameJobSkillDraft(
  skills: { skill_id: string; skill_name: string }[],
  editingSkillId: string,
  rawNewName: string
): PrepareRenameJobSkillResult {
  const current = skills.find((s) => s.skill_id === editingSkillId)
  if (!current) return { ok: false, error: 'Skill not found on this job.' }

  const trimmed = rawNewName.trim()
  if (!trimmed) return { ok: false, error: 'Enter a skill name.' }
  if (trimmed.toLowerCase() === current.skill_name.trim().toLowerCase()) {
    return { ok: false, error: null }
  }

  const duplicate = skills.some(
    (s) =>
      s.skill_id !== editingSkillId &&
      s.skill_name.trim().toLowerCase() === trimmed.toLowerCase()
  )
  if (duplicate) {
    return { ok: false, error: `"${trimmed}" is already on this job.` }
  }

  return { ok: true, trimmed }
}

export type PrepareAppendJobSkillResult = PrepareRenameJobSkillResult

/** Validates append draft for a job skill row. */
export function prepareAppendJobSkillDraft(
  skills: { skill_id: string; skill_name: string }[],
  rawName: string
): PrepareAppendJobSkillResult {
  const trimmed = rawName.trim()
  if (!trimmed) return { ok: false, error: 'Enter a skill name.' }
  if (skills.some((s) => s.skill_name.trim().toLowerCase() === trimmed.toLowerCase())) {
    return { ok: false, error: `"${trimmed}" is already on this job.` }
  }
  return { ok: true, trimmed }
}

export function serializeJobSkillsForCompare(
  skills: { skill_id?: string; id?: string; requirement_type: string }[]
): string {
  return JSON.stringify(
    skills
      .map((s) => ({ id: s.skill_id ?? s.id, rt: s.requirement_type }))
      .sort((a, b) => String(a.id).localeCompare(String(b.id)))
  )
}
